import { Alert, Button, Modal, Space, Typography } from "antd";
import type { Customer, ServiceCostBill } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { BILL_STATE, SERVICE_COST_SOURCE, SERVICE_COST_TYPE } from "@/meta/expenseManage/serviceCost";
import { Permission } from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";
import {
    ServiceCostBillVerificationModal,
    type ServiceCostBillVerificationFile,
    type ServiceCostBillVerificationValues
} from "./ServiceCostBillVerificationModal";

interface ServiceCostBillDetailData extends Partial<ServiceCostBill> {
    verificationName?: string;
}

export interface ServiceCostBillDetailPayload {
    serviceCostBill: ServiceCostBillDetailData;
    customer: Partial<Customer>;
}

export interface ServiceCostBillDetailPageProps {
    billId?: number | string;
    loadBill?: (id: number | string) => Promise<ServiceCostBillDetailPayload>;
    verifyBill?: (params: {
        id: number | string;
        verificationAmount: number;
        verificationTime: string;
        verificationFileId?: number;
    }) => Promise<unknown>;
    revokeBill?: (id: number | string) => Promise<unknown>;
    uploadFile?: (file: Blob) => Promise<ServiceCostBillVerificationFile>;
    loadVerificationFile?: (id: number | string) => Promise<{ path?: string }>;
}

/** 复用 Vue 服务费账单详情接口。 */
export async function loadServiceCostBillDetail(id: number | string): Promise<ServiceCostBillDetailPayload> {
    const response = await request.service.get<ServiceCostBillDetailPayload>(`/admin/service-cost-bill/vo/${id}`);
    return response.data;
}

/** 复用 Vue 服务费账单核销接口。 */
export async function verifyServiceCostBill(params: {
    id: number | string;
    verificationAmount: number;
    verificationTime: string;
    verificationFileId?: number;
}) {
    const response = await request.service.put("/admin/service-cost-bill/verification", params);
    return response.data;
}

/** 复用 Vue 服务费账单撤销接口。 */
export async function revokeServiceCostBill(id: number | string) {
    const response = await request.service.put(`/admin/service-cost-bill/revoke/${id}`);
    return response.data;
}

/** 复用 Vue 核销凭证查看接口，返回可直接打开的文件地址。 */
export async function loadServiceCostBillVerificationFile(id: number | string): Promise<{ path?: string }> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${Number(id)}`);
    return response.data;
}

const display = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : value;
const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatAmount = (value?: number | null) =>
    value === undefined || value === null
        ? DEFAULT_VALUE
        : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatCustomer = (customer?: Partial<Customer>) =>
    customer
        ? `${customer.institutionName || customer.realName || DEFAULT_VALUE} | ${customer.mobile || DEFAULT_VALUE} | ${customer.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;

/** 费用管理-服务费账单详情，覆盖 Vue 用户、结算方式和核销状态分支。 */
export function ServiceCostBillDetailPage({
    billId,
    loadBill = loadServiceCostBillDetail,
    verifyBill = verifyServiceCostBill,
    revokeBill = revokeServiceCostBill,
    uploadFile,
    loadVerificationFile = loadServiceCostBillVerificationFile
}: ServiceCostBillDetailPageProps) {
    const params = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const currentId = billId ?? params.id ?? "";
    const [payload, setPayload] = useState<ServiceCostBillDetailPayload>();
    const [errorMessage, setErrorMessage] = useState("");
    const [verificationOpen, setVerificationOpen] = useState(false);
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [revokeOpen, setRevokeOpen] = useState(false);
    const [revokeLoading, setRevokeLoading] = useState(false);

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadBill(currentId)
            .then(value => {
                if (!disposed) setPayload(value);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("服务费账单详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, loadBill]);

    const bill = payload?.serviceCostBill;
    const isVerified = bill?.verificationState === BILL_STATE.WRITTEN_OFF.code;

    // 按 Vue 核销弹窗输出接口参数，成功后重新加载详情以刷新状态和金额。
    const submitVerification = async (values: ServiceCostBillVerificationValues) => {
        setVerificationLoading(true);
        try {
            await verifyBill({
                id: currentId,
                verificationAmount: values.amount,
                verificationTime: values.date,
                verificationFileId: values.fileId
            });
            const refreshed = await loadBill(currentId);
            setPayload(refreshed);
            setVerificationOpen(false);
        } catch {
            setErrorMessage("账单核销失败");
        } finally {
            setVerificationLoading(false);
        }
    };

    // 按 Vue 撤销流程保留二次确认，成功后刷新当前账单详情和核销状态。
    const submitRevoke = async () => {
        setRevokeLoading(true);
        setErrorMessage("");
        try {
            await revokeBill(currentId);
            const refreshed = await loadBill(currentId);
            setPayload(refreshed);
            setRevokeOpen(false);
        } catch {
            setErrorMessage("账单撤销失败");
        } finally {
            setRevokeLoading(false);
        }
    };

    // 保留 Vue base-item 的凭证查看行为，先取文件地址再在新窗口打开。
    const previewVerificationFile = async (fileId: number) => {
        try {
            const file = await loadVerificationFile(fileId);
            if (file.path) window.open(file.path, "_blank");
        } catch {
            setErrorMessage("核销凭证加载失败");
        }
    };

    return (
        <section aria-label="service-cost-bill-detail-page" className="flex min-h-full flex-col gap-4">
            <ZhDetailHeader
                leftInfos={[{ label: "编号", value: display(bill?.id) }]}
                rightInfos={
                    isVerified
                        ? [
                              { label: "核销专员", value: display(bill?.verificationName) },
                              { label: "创建时间", value: formatDate(bill?.createdTime) }
                          ]
                        : [{ label: "创建时间", value: formatDate(bill?.createdTime) }]
                }
                stateDesc={BILL_STATE.getIntroByCode(bill?.verificationState) || DEFAULT_VALUE}
                title="账单详情"
            />
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            <div className="section-card">
                <div className="section-title mb-20">用户账户</div>
                <ZhGrid cols={4} gap={[20, 30]}>
                    <ZhBaseItem label="用户" span={4}>
                        <span>{formatCustomer(payload?.customer)}</span>
                    </ZhBaseItem>
                </ZhGrid>
            </div>
            <div className="section-card">
                <div className="section-title mb-20">相关信息</div>
                <ZhGrid cols={4} gap={[20, 30]}>
                    <ZhBaseItem
                        label="来源"
                        value={SERVICE_COST_SOURCE.getIntroByCode(bill?.source) || DEFAULT_VALUE}
                    />
                    <ZhBaseItem
                        label="关联项目"
                        value={
                            bill?.projectId ? (
                                <Link to={`/purchasingManage/project/info/${bill.projectId}`}>
                                    {bill.projectName || DEFAULT_VALUE}
                                </Link>
                            ) : (
                                display(bill?.projectName)
                            )
                        }
                    />
                    {bill && bill.type !== SERVICE_COST_TYPE.FIXED_PRICE.code ? (
                        <ZhBaseItem
                            label="关联对账单"
                            value={
                                <Link to={`/purchasingManage/reconciliation/info/${bill.purchaseReconciliationId}`}>
                                    {display(bill.purchaseReconciliationId)}
                                </Link>
                            }
                        />
                    ) : null}
                    <ZhBaseItem
                        label="结算方式"
                        value={SERVICE_COST_TYPE.getIntroByCode(bill?.type) || DEFAULT_VALUE}
                    />
                    {bill && bill.type !== SERVICE_COST_TYPE.FIXED_PRICE.code ? (
                        <ZhBaseItem label="单价" value={`${formatAmount(bill.unitPrice)}元/吨`} />
                    ) : null}
                    {bill && bill.type !== SERVICE_COST_TYPE.FIXED_PRICE.code ? (
                        <ZhBaseItem label="吨数" value={`${display(bill.ton)}吨`} />
                    ) : null}
                </ZhGrid>
            </div>
            <div className="section-card">
                <div className="section-title mb-20">{isVerified ? "核销信息" : "账单信息"}</div>
                <ZhGrid cols={4} gap={[20, 30]}>
                    {isVerified ? (
                        <>
                            <ZhBaseItem label="日期" value={formatDate(bill?.verificationTime)} />
                            <ZhBaseItem label="账单金额" value={formatAmount(bill?.amount)} />
                            <ZhBaseItem label="实际核销金额" value={formatAmount(bill?.verificationAmount)} />
                            <ZhBaseItem label="核销专员" value={display(bill?.verificationName)} />
                            {bill?.verificationFileId ? (
                                <ZhBaseItem label="核销凭证">
                                    <Button
                                        aria-label="查看/下载核销凭证"
                                        type="link"
                                        onClick={() => void previewVerificationFile(bill.verificationFileId as number)}
                                    >
                                        查看/下载
                                    </Button>
                                </ZhBaseItem>
                            ) : null}
                        </>
                    ) : (
                        <ZhBaseItem label="金额" value={formatAmount(bill?.amount)} />
                    )}
                </ZhGrid>
            </div>
            <Space>
                {bill && !isVerified ? (
                    <Permission anyOf={["ROLE_ACCOUNT_VERIFY"]}>
                        <Button disabled={banButton} type="primary" onClick={() => setVerificationOpen(true)}>
                            核销
                        </Button>
                    </Permission>
                ) : null}
                {bill && isVerified ? (
                    <Permission anyOf={["ROLE_ACCOUNT_VERIFY"]}>
                        <Button disabled={banButton} type="primary" onClick={() => setRevokeOpen(true)}>
                            撤销
                        </Button>
                    </Permission>
                ) : null}
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
            <ServiceCostBillVerificationModal
                open={verificationOpen}
                billAmount={bill?.amount}
                loading={verificationLoading}
                disabled={banButton}
                onCancel={() => setVerificationOpen(false)}
                onSubmit={values => void submitVerification(values)}
                uploadFile={uploadFile}
            />
            <Modal
                title="撤销账单"
                open={revokeOpen}
                okText="确定"
                cancelText="取消"
                confirmLoading={revokeLoading}
                okButtonProps={{ disabled: banButton }}
                onCancel={() => setRevokeOpen(false)}
                onOk={() => void submitRevoke()}
            >
                <Typography.Text>是否撤销，撤销后账户余额将发生变化，请谨慎操作</Typography.Text>
            </Modal>
        </section>
    );
}
